import { useAppState } from '@/hooks/useAppState';
import { http } from '@/utils/http';
import {
  AcceptFrisbiiSessionSchema,
  CancelFrisbiiSessionSchema,
  CreateFrisbiiSessionSchemaFrontend,
} from '@/utils/validations/frisbii';
import { AxiosError } from 'axios';
import { FrisbiiError } from 'lib/errors/FrisbiiError';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import useWindowDataLayer from './useWindowDataLayer';

export type SessionPayload = z.infer<typeof CreateFrisbiiSessionSchemaFrontend>;
export type AcceptSessionPayload = z.infer<typeof AcceptFrisbiiSessionSchema> & Record<string, any>;
export type CancelSessionPayload = z.infer<typeof CancelFrisbiiSessionSchema> & Record<string, any>;

export const useFrisbiiPayment = () => {
  const [{ frisbiiSession }, appStateDispatcher] = useAppState();
  const { push: dataLayerPush } = useWindowDataLayer();

  const dispatchFrisbiiSession = useCallback(
    (newSession: Partial<typeof frisbiiSession> | undefined) => {
      appStateDispatcher({
        frisbiiSession: !newSession
          ? newSession
          : {
              ...frisbiiSession,
              ...newSession,
            },
      });
    },
    [appStateDispatcher, frisbiiSession],
  );

  const createFrisbiiSession = useCallback(
    async (payload: Partial<SessionPayload> = {}) => {
      const response = await http
        .post<{ session_id: string; signature: string }>(
          '/api/payments/v2/create-frisbii-session',
          { ...payload, referrer: payload.referrer || window.location.href },
          {
            skipInterceptors: { response: { rejected: true } },
          },
        )
        .catch((error: AxiosError) => {
          if (error.response?.status === 409) {
            throw new FrisbiiError('account_exists', error);
          }

          throw new FrisbiiError('unknown', error);
        });

      if (!response?.data) {
        throw new FrisbiiError('unknown');
      }

      const session = {
        sessionId: response.data.session_id,
        signature: response.data.signature,
        purchaseCompleted: false,
      };
      dispatchFrisbiiSession(session);

      return session;
    },
    [dispatchFrisbiiSession],
  );

  const onAcceptEvent = useCallback(async (payload: AcceptSessionPayload) => {
    const response = await http.post<{ redirect_to: string }>('/api/payments/v2/accept-frisbii-session', payload);
    if (!response?.data) {
      throw new Error('Failed to accept payment session');
    }

    dataLayerPush({
      event: 'purchase',
      ecommerce: {
        transaction_id: payload.subscription || '',
        subscription_id: payload.subscription || '',
        currency: 'sek',
        value: 29,
        items: [{ price: 29, item_name: payload.plan, item_id: payload.plan }],
      },
    });
    dispatchFrisbiiSession({ purchaseCompleted: true });

    return response.data;
  }, []);

  const onCancelEvent = useCallback(async (payload: CancelSessionPayload) => {
    await http.post('/api/payments/v2/cancel-frisbii-session', payload);
    dispatchFrisbiiSession(undefined);
  }, []);

  const onErrorFrisbiiEvent = useCallback(async (errorData: Record<string, unknown> = {}) => {
    console.error('Frisbii payment error event:', errorData);
    dispatchFrisbiiSession(undefined);
  }, []);

  return useMemo(
    () => ({
      createFrisbiiSession,
      onAcceptEvent,
      onCancelEvent,
      onErrorFrisbiiEvent,
      frisbiiSession,
    }),
    [createFrisbiiSession, onAcceptEvent, onCancelEvent, onErrorFrisbiiEvent, frisbiiSession, appStateDispatcher],
  );
};
